import * as React from 'react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useGradeStudentInterviewMutation } from '@/store/instructor/interview';

export interface InstructorAttempt {
    attemptId: string;
    status?: string;
    name: string;
    email?: string;
    studentId?: string;
    technicalScore?: number;
    communicationScore?: number;
    problemSolvingScore?: number;
    feedback?: string;
    overallScore?: number;
}

interface InterviewCardInstructorAgreedListProps {
    attempts: InstructorAttempt[];
    token?: string | null;
    instructorId?: string | null;
}

const InterviewCardInstructorAgreedList: React.FC<InterviewCardInstructorAgreedListProps> = ({
    attempts,
    token,
    instructorId,
}) => {
    const [openAttemptId, setOpenAttemptId] = React.useState<string | null>(null);
    const [formState, setFormState] = React.useState<Record<string, {
        technicalScore: number | '';
        communicationScore: number | '';
        problemSolvingScore: number | '';
        feedback: string;
    }>>({});

    const [gradeStudentInterview, { isLoading: isSubmitting }] = useGradeStudentInterviewMutation();

    if (attempts.length === 0) {
        return null;
    }

    const toggleAttempt = (attemptId: string) => {
        setOpenAttemptId((prev) => (prev === attemptId ? null : attemptId));
    };

    const updateForm = (attemptId: string, updates: Partial<{
        technicalScore: number | '';
        communicationScore: number | '';
        problemSolvingScore: number | '';
        feedback: string;
    }>) => {
        setFormState((prev) => {
            const current = prev[attemptId] ?? {
                technicalScore: '' as const,
                communicationScore: '' as const,
                problemSolvingScore: '' as const,
                feedback: '',
            };
            return {
                ...prev,
                [attemptId]: { ...current, ...updates },
            };
        });
    };

    const handleSubmit = async (attemptId: string) => {
        if (!token || !instructorId) {
            toast.error('You must be logged in as an instructor');
            return;
        }

        const attemptForm = formState[attemptId];
        if (!attemptForm) {
            toast.error('Fill in the grading form');
            return;
        }

        const { technicalScore, communicationScore, problemSolvingScore, feedback } = attemptForm;
        if (technicalScore === '' || communicationScore === '' || problemSolvingScore === '') {
            toast.error('Provide all scores before submitting');
            return;
        }

        try {
            await gradeStudentInterview({
                studentInterviewId: attemptId,
                Token: token,
                formData: {
                    technicalScore,
                    communicationScore,
                    problemSolvingScore,
                    feedback: feedback || '',
                    instructorId,
                },
            }).unwrap();
            toast.success('Grade submitted');
            setOpenAttemptId(null);
        } catch (error: any) {
            console.error('Error grading interview', error);
            toast.error(error?.data?.message || error?.message || 'Failed to submit grade');
        }
    };

    return (
        <div className="space-y-3">
            <p className="text-sm font-semibold text-gray-700 dark:text-gray-100">
                Agreed Students ({attempts.length})
            </p>
            <div className="space-y-3">
                {attempts.map((attempt) => {
                    const isCompleted = attempt.status === 'completed';
                    const average = typeof attempt.overallScore === 'number'
                        ? `${attempt.overallScore}%`
                        : '-';
                    const currentForm = formState[attempt.attemptId] ?? {
                        technicalScore: attempt.technicalScore ?? '',
                        communicationScore: attempt.communicationScore ?? '',
                        problemSolvingScore: attempt.problemSolvingScore ?? '',
                        feedback: attempt.feedback ?? '',
                    };

                    return (
                        <div
                            key={attempt.attemptId}
                            className="rounded-md border border-emerald-100 bg-white p-4 shadow-sm dark:bg-green-100"
                        >
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                <div className="text-sm text-gray-700">
                                    <strong>{attempt.name}</strong>{' '}
                                    <span className="text-gray-500">({attempt.email || 'Unknown'})</span>
                                    <div className="text-xs text-gray-500">
                                        Status:{' '}
                                        <span className={isCompleted ? 'text-emerald-600' : 'text-blue-600'}>
                                            {attempt.status}
                                        </span>
                                        {isCompleted ? (
                                            <>
                                                {' '}| Avg:{' '}
                                                <span className="font-semibold text-purple-700">
                                                    {average}
                                                </span>
                                            </>
                                        ) : null}
                                    </div>
                                </div>
                                {!isCompleted ? (
                                    <Button
                                        variant={openAttemptId === attempt.attemptId ? 'close' : 'open'}
                                        size="sm"
                                        onClick={() => toggleAttempt(attempt.attemptId)}
                                    >
                                        {openAttemptId === attempt.attemptId ? 'Close' : 'Provide Grade'}
                                    </Button>
                                ) : null}
                            </div>                                                                                                                                                              

                            {openAttemptId === attempt.attemptId && !isCompleted ? (
                                <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                                    <div>
                                        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-600">
                                            Technical (0-100)
                                        </label>
                                        <input
                                            type="number"
                                            min={0}
                                            max={100}
                                            className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200 dark:text-gray-900"
                                            value={currentForm.technicalScore}
                                            onChange={(event) => updateForm(attempt.attemptId, {
                                                technicalScore: event.target.value === '' ? '' : Number(event.target.value),
                                            })}
                                        />
                                    </div>
                                    <div>
                                        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-600">
                                            Communication (0-100)
                                        </label>
                                        <input
                                            type="number"
                                            min={0}
                                            max={100}
                                            className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200 dark:text-gray-900"
                                            value={currentForm.communicationScore}
                                            onChange={(event) => updateForm(attempt.attemptId, {
                                                communicationScore: event.target.value === '' ? '' : Number(event.target.value),
                                            })}
                                        />
                                    </div>
                                    <div>
                                        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-600">
                                            Problem Solving (0-100)
                                        </label>
                                        <input
                                            type="number"
                                            min={0}
                                            max={100}
                                            className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200 dark:text-gray-900"
                                            value={currentForm.problemSolvingScore}
                                            onChange={(event) => updateForm(attempt.attemptId, {
                                                problemSolvingScore: event.target.value === '' ? '' : Number(event.target.value),
                                            })}
                                        />
                                    </div>
                                    <div className="sm:col-span-2 lg:col-span-4">
                                        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-600">
                                            Feedback
                                        </label>
                                        <textarea
                                            rows={2}
                                            className="w-full rounded border border-gray-200 px-3 py-2 text-sm shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200 dark:text-gray-900"
                                            value={currentForm.feedback}
                                            onChange={(event) => updateForm(attempt.attemptId, {
                                                feedback: event.target.value,
                                            })}
                                        />
                                    </div>
                                    <div className="sm:col-span-2 lg:col-span-4 flex justify-end ">
                                        <Button
                                            onClick={() => handleSubmit(attempt.attemptId)}
                                            className='cursor-pointer bg-blue-500 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-800 dark:text-gray-100'
                                            disabled={
                                                isSubmitting ||
                                                currentForm.technicalScore === '' ||
                                                currentForm.communicationScore === '' ||
                                                currentForm.problemSolvingScore === ''
                                            }
                                        >
                                            {isSubmitting ? 'Submitting...' : 'Submit Grade'}
                                        </Button>
                                    </div>
                                </div>
                            ) : null}

                            {isCompleted ? (
                                <div className="mt-3 text-xs text-gray-600">
                                    <div>
                                        Technical: <strong>{attempt.technicalScore ?? '-'}</strong> | Communication:{' '}
                                        <strong>{attempt.communicationScore ?? '-'}</strong> | Problem Solving:{' '}
                                        <strong>{attempt.problemSolvingScore ?? '-'}</strong>
                                    </div>
                                    {attempt.feedback ? (
                                        <div>
                                            Feedback: <span className="italic">{attempt.feedback}</span>
                                        </div>
                                    ) : null}
                                </div>
                            ) : null}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default InterviewCardInstructorAgreedList;

